// src/components/TopMenuBar.jsx
"use client";

import {
  Flex,
  Box,
  Link,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
  Image,
} from "@chakra-ui/react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDownIcon, HamburgerIcon } from "@chakra-ui/icons";
import { adminRoutes } from "routes";
import AdminNavbarLinks from "components/navbar/NavbarLinksAdmin";
import { useAuth } from "contexts/AuthContext";

function joinPath(layout, path) {
  const L = String(layout || "").replace(/\/$/, "");
  const P = String(path || "").replace(/^\//, "");
  return `${L}/${P}`;
}

/** 파란 밑줄(한컴 느낌) 하이라이트/호버 컴포넌트 */
function Underline({ children, isActive, color }) {
  return (
    <Box
      as="span"
      position="relative"
      display="inline-block"
      _after={{
        content: '""',
        position: "absolute",
        left: 0,
        bottom: "-4px",
        height: "2px",
        width: isActive ? "100%" : "0%",
        bg: color,
        transition: "width 200ms ease",
      }}
      _hover={{ _after: { width: "100%" } }}
    >
      {children}
    </Box>
  );
}

export default function TopMenuBar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const { user = {} } = useAuth?.() || {};
  const isAdmin = ["admin", "super_admin"].includes(user?.role);

  const bgGradient = useColorModeValue("linear(to-r, white, gray.50)", "linear(to-r, gray.800, gray.900)");
  const textColor = useColorModeValue("gray.700", "white");
  const activeColor = useColorModeValue("blue.600", "blue.300");
  const menuBg = useColorModeValue("white", "gray.700");
  const menuHoverBg = useColorModeValue("blue.50", "gray.600");
  const borderColor = useColorModeValue("gray.100", "gray.600");
  const underlineColor = useColorModeValue("blue.600", "blue.300");
  // hidden 라우트 제거
  const visibleRoutes = (adminRoutes || [])
    .filter((r) => !r.hidden && (r.path !== "users" || isAdmin))
    .map((r) =>
      r.children ? { ...r, children: (r.children || []).filter((c) => !c.hidden) } : r
    );

  return (
    <Flex
      as="nav"
      align="center"
      justify="center"
      px={{ base: "16px", md: "40px" }}
      py="16px"
      bgGradient={bgGradient}
      color={textColor}
      // ❌ 요청대로 그림자 & 보더 제거
      // boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      // borderBottom="1px solid"
      // borderColor={borderColor}
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex="1000"
      backdropFilter="blur(10px)"
    >
    <Flex align="center" justify="space-between" w="100%" gap={{ base: "4px", md: "8px" }}>
        {/* 로고 */}
          <Flex
            as={NavLink}
            to="/admin/prediction"
            alignItems="center"
            _hover={{ textDecoration: "none" }}
          >
            <Image
              src="/logo_snd.png"
              alt="logo"
              h={{ base: "20px", sm: "22px", md: "26px", lg: "30px" }}
              objectFit="contain"
              draggable={false}
            />
          </Flex>

        {/* 데스크톱 메뉴 */}
        <Flex
          gap="32px"
          as="ul"
          listStyleType="none"
          display={{ base: "none", lg: "flex" }}
          justify="center"
          flex="3"
        >
          {visibleRoutes.map((route, idx) => {
            if (route.children && route.children.length > 0) {
              return (
                <Menu key={`menu-${idx}`} isLazy>
                  <MenuButton
                    fontSize="sm"
                    fontWeight="medium"
                    color="gray.600"
                    _hover={{ color: activeColor }}
                  >
                    <Underline color={underlineColor}>{route.name}</Underline> <ChevronDownIcon ml="1" />
                  </MenuButton>
                  <MenuList bg={menuBg} border="1px solid" borderColor={borderColor}>
                    {route.children.map((child, cidx) => (
                      <MenuItem
                        key={`menu-item-${idx}-${cidx}`}
                        as={NavLink}
                        to={joinPath(child.layout, child.path)}
                        _hover={{ bg: menuHoverBg }}
                      >
                        {child.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              );
            }

            const to = joinPath(route.layout, route.path);
            const isActive = location.pathname === to || location.pathname.startsWith(to + "/");

            return (
              <Link
                as={NavLink}
                key={`link-${idx}`}
                to={to}
                fontSize="sm"
                fontWeight="medium"
                color="gray.600"
                _hover={{ color: activeColor }}
                _activeLink={{ color: activeColor, fontWeight: "600" }}
              >
                <Underline isActive={isActive} color={underlineColor}>
                  {route.name}
                </Underline>
              </Link>
            );
          })}
        </Flex>

        {/* 우측 */}
        <Flex align="center" gap={{ base: "8px", md: "20px" }} flex="1" justify="flex-end">
          <Box display={{ base: "none", lg: "block" }}>
            <AdminNavbarLinks />
          </Box>
          {/* 햄버거 버튼(요청상 없어도 괜찮음 — 구조 유지, 스타일 변경 없음) */}
          <IconButton
            display={{ base: "flex", lg: "none" }}
            aria-label="Open menu"
            icon={<HamburgerIcon />}
            onClick={onOpen}
            variant="ghost"
            size="sm"
          />
        </Flex>
      </Flex>

      {/* 모바일 Drawer (상단 보더만 제거) */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg={menuBg}>
          <DrawerCloseButton />
          <DrawerHeader /* borderBottomWidth="1px" borderColor={borderColor} */>메뉴</DrawerHeader>
          <DrawerBody>
            <VStack spacing={4} align="stretch" pt={4}>
              <Box /* pb={4} borderBottom="1px solid" borderColor={borderColor} */>
                <AdminNavbarLinks />
              </Box>

              {visibleRoutes.map((route, idx) => {
                if (route.children && route.children.length > 0) {
                  return (
                    <Box key={`m-wrap-${idx}`}>
                      {route.children.map((child, cidx) => (
                        <Link
                          key={`m-child-${idx}-${cidx}`}
                          as={NavLink}
                          to={joinPath(child.layout, child.path)}
                          onClick={onClose}
                          _activeLink={{ fontWeight: "600", color: activeColor, bg: menuHoverBg }}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </Box>
                  );
                }

                return (
                  <Link
                    as={NavLink}
                    key={`m-link-${idx}`}
                    to={joinPath(route.layout, route.path)}
                    onClick={onClose}
                    _activeLink={{ fontWeight: "600", color: activeColor, bg: menuHoverBg }}
                  >
                    {route.name}
                  </Link>
                );
              })}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}
